import React, { useState, useEffect } from "react";
import { Button, Form, Table, OverlayTrigger, Tooltip, Image } from "react-bootstrap";
import infoImg from "../../../../../../assets/infoimg.png";
import ArbitrationFormModal from "./ArbitrationFormModal";
import ViewFileModal from "./ViewFileModal";

const RequiredDocuments = ({ formData, setFormData, showValidation }) => {
    const [uploadedFiles, setUploadedFiles] = useState({});
    const [uploadErrors, setUploadErrors] = useState({});
    const [isFormValid, setIsFormValid] = useState(false);
    const [showArbitrationForm, setShowArbitrationForm] = useState(false);
    const [viewFile, setViewFile] = useState(null);

    // Tooltips for each document type
    const tooltips = {
        arbitrationForm: "Upload Arbitration Form A1 (Fill in or Upload)\nYou can directly fill in the online form or can upload saved copy of a completed and signed form (PDF, DOC, Word), with a maximum file size: 5 MB",
        correspondenceWithSupplier: "Upload Correspondence with Buyer/Supplier\nYou can include emails, letters, chats or screenshots. Save all the items/documents in a single file or documents into PDF or JPG, JPEG, PNG, DOC. Max file size: 5 MB",
        commercialInvoice: "Upload Commercial Invoice\nUpload the official invoice in PDF, JPG, JPEG, PNG, DOC formats. Add Buyer/Supplier name, Invoice number & date and Product/service details and amount in INR and Foreign Currency. Max file size: 5 MB",
        inspectionReports: "Upload Inspection Reports\nUpload relevant reports from: Quality control, third-party inspections or internal checks in PDF, JPG, JPEG, PNG, DOC. Ensure reports are signed, dated, and clearly legible. Max file size: 5 MB",
        correspondenceWithAgent: "Upload Correspondence with Agent\nUpload email exchanges or letters or WhatsApp conversation between you and your agent in PDF, JPG, JPEG, PNG, DOC format. Combine multiple pages into one file if needed. Max file size: 5 MB",
        correspondenceWithCarrier: "Upload Correspondence with Carrier\nUpload communication with the shipping/freight company regarding tracking issues, delays, claims, etc. in PDF, JPG, JPEG, PNG, DOC. Max file size: 5 MB",
        photographs: "Photographs / Witness Statements\nPhotograph Must be clear and relevant to the case in the standard Indian passport photo dimensions are 51 mm x 51 mm. Witness statements Should be signed and dated properly. Preferred file formats are- JPG, JPEG, PNG, PDF, DOC. Max file size: 5 MB",
        legalActionDocuments: "Legal Action Documents\nUpload any legal initiatives, notices, FIR copies, court orders, or case documents. Make sure the documents are properly scanned and saved in PDF, JPG, JPEG, PNG, DOC. Max file size: 5 MB"
    };

    // Initialize uploadedFiles from formData when component mounts
    useEffect(() => {
        const initialFiles = {};
        Object.keys(formData).forEach(key => {
            if (formData[key] && typeof formData[key] === 'object' && formData[key] instanceof File) {
                initialFiles[key] = formData[key];
            }
        });
        setUploadedFiles(initialFiles);
    }, [formData]);

    const handleFileUpload = async (e, documentType) => {
        const file = e.target.files[0];
        setUploadErrors(prev => ({ ...prev, [documentType]: "" }));

        if (!file) return;

        // Check file type
        const allowedTypes = [
            'application/pdf',
            'image/jpeg',
            'image/jpg',
            'image/png',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ];

        if (!allowedTypes.includes(file.type)) {
            setUploadErrors(prev => ({
                ...prev,
                [documentType]: "Only PDF, images (JPG, PNG) and Word documents are allowed"
            }));
            return;
        }

        // Check file size (5MB = 5 * 1024 * 1024 bytes)
        if (file.size > 5 * 1024 * 1024) {
            setUploadErrors(prev => ({
                ...prev,
                [documentType]: "File size should not exceed 5MB"
            }));
            return;
        }

        setUploadedFiles(prev => ({
            ...prev,
            [documentType]: file
        }));

        setFormData(prev => ({
            ...prev,
            [documentType]: file
        }));
    };

    // Check if all required documents are uploaded
    useEffect(() => {
        const requiredDocuments = [
            "arbitrationForm",
            "correspondenceWithSupplier",
            "commercialInvoice"
        ];

        const isAllRequiredUploaded = requiredDocuments.every(doc => uploadedFiles[doc] !== undefined);
        setIsFormValid(isAllRequiredUploaded);
    }, [uploadedFiles]);

    const renderDocumentRow = (title, documentType, isRequired, showFillForm = false) => (
        <tr>
            <td className="align-middle">
                <div className="fw-semibold d-flex align-items-center gap-2">
                    <span>
                        {title}
                        {isRequired && <span className="text-danger ms-1">*</span>}
                    </span>
                    <OverlayTrigger
                        placement="right"
                        overlay={<Tooltip id={`${documentType}-tooltip`}>{tooltips[documentType]}</Tooltip>}
                    >
                        {({ ref, ...triggerHandler }) => (
                            <Button
                                variant="light"
                                {...triggerHandler}
                                className="d-inline-flex align-items-center p-0"
                                style={{ backgroundColor: "transparent", border: "none" }}
                            >
                                <Image
                                    ref={ref}
                                    roundedCircle
                                    src={infoImg}
                                    style={{ height: "3vh" }}
                                    alt=""
                                />
                            </Button>
                        )}
                    </OverlayTrigger>
                </div>
            </td>
            <td className="align-middle">
                <div className="d-flex justify-content-end gap-2">
                    {showFillForm && (
                        <Button
                            variant="info"
                            size="sm"
                            onClick={() => setShowArbitrationForm(true)}
                        >
                            Fill Form
                        </Button>
                    )}
                    <div className="d-flex gap-2">
                        <Form.Control
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            onChange={(e) => handleFileUpload(e, documentType)}
                            style={{ display: "none" }}
                            id={`${documentType}-upload`}
                        />
                        <Button
                            variant="success"
                            size="sm"
                            onClick={() => document.getElementById(`${documentType}-upload`).click()}
                        >
                            {uploadedFiles[documentType] ? "Change File" : "Upload"}
                        </Button>
                        {uploadedFiles[documentType] && (
                            <>
                                <Button
                                    variant="info"
                                    size="sm"
                                    onClick={() => setViewFile(uploadedFiles[documentType])}
                                >
                                    View
                                </Button>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => {
                                        setUploadedFiles(prev => {
                                            const updated = { ...prev };
                                            delete updated[documentType];
                                            return updated;
                                        });
                                        setFormData(prev => ({
                                            ...prev,
                                            [documentType]: null
                                        }));
                                    }}
                                >
                                    Delete
                                </Button>
                            </>
                        )}
                    </div>
                </div>
                {uploadErrors[documentType] && (
                    <div className="mt-1">
                        <small className="text-danger">{uploadErrors[documentType]}</small>
                    </div>
                )}
                {showValidation && isRequired && !uploadedFiles[documentType] && (
                    <div className="mt-1">
                        <small className="text-danger">This document is required</small>
                    </div>
                )}
            </td>
        </tr>
    );

    return (
        <div className="mt-3">
            <Table bordered hover className="align-middle">
                <thead className="table-light">
                    <tr>
                        <th style={{ width: '50%' }}>Document Name</th>
                        <th style={{ width: '50%' }}>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {renderDocumentRow("Arbitration Form - A1", "arbitrationForm", true, true)}
                    {renderDocumentRow("Correspondence with supplier / Buyer", "correspondenceWithSupplier", true)}
                    {renderDocumentRow("Commercial Invoice", "commercialInvoice", true)}
                    {renderDocumentRow("Inspection Reports", "inspectionReports", false)}
                    {renderDocumentRow("Correspondence with Agent", "correspondenceWithAgent", false)}
                    {renderDocumentRow("Correspondence with Carrier", "correspondenceWithCarrier", false)}
                    {renderDocumentRow("Photographs / Witness Statements", "photographs", false)}
                    {renderDocumentRow("Legal Action Documents", "legalActionDocuments", false)}
                </tbody>
            </Table>

            {!isFormValid && showValidation && (
                <div className="text-danger mt-3">
                    Please upload all required documents before proceeding to the next step.
                </div>
            )}

            <ArbitrationFormModal
                show={showArbitrationForm}
                handleClose={() => setShowArbitrationForm(false)}
            />

            <ViewFileModal
                show={!!viewFile}
                handleClose={() => setViewFile(null)}
                file={viewFile}
            />
        </div>
    );
};

export default RequiredDocuments;